import {
    Box,
    Button,
    Dialog,
    Flex,
    Spinner,
    Text,
} from "@chakra-ui/react";

import {
    Download,
    X,
} from "lucide-react";

import {
    useEffect,
    useMemo,
    useState,
} from "react";


const ResourceDocumentDialog = ({
    isOpen,
    onClose,
    resource,
    title,
    description = "Review the document before downloading.",
    downloadFileName = "Document",
}) => {
    const API_URL = useMemo(
        () =>
            process.env.REACT_APP_API_BASE_URL?.replace(
                /\/$/,
                ""
            ),
        []
    );

    const [
        viewerLoading,
        setViewerLoading,
    ] = useState(true);

    const [
        viewerError,
        setViewerError,
    ] = useState(false);

    const documentFile =
        resource?.documents?.[0];

    const documentUrl =
        documentFile?.file_path
            ? `${API_URL}/${documentFile.file_path.replace(
                  /^\//,
                  ""
              )}`
            : null;


    /*
     * Reset the viewer every time
     * the dialog opens or the document
     * changes.
     */
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setViewerLoading(true);
        setViewerError(false);
    }, [
        isOpen,
        documentUrl,
    ]);


    const handleDownload =
        async () => {
            if (!documentUrl) {
                return;
            }

            try {
                const response =
                    await fetch(
                        documentUrl
                    );

                if (!response.ok) {
                    throw new Error(
                        "Failed to download document"
                    );
                }

                const blob =
                    await response.blob();

                const url =
                    window.URL.createObjectURL(
                        blob
                    );

                const link =
                    document.createElement(
                        "a"
                    );

                link.href = url;

                link.download =
                    downloadFileName;

                document.body.appendChild(
                    link
                );

                link.click();

                document.body.removeChild(
                    link
                );

                window.URL.revokeObjectURL(
                    url
                );
            } catch (error) {
                console.error(
                    "Failed to download document:",
                    error
                );
            }
        };


    const googleViewerUrl =
        documentUrl
            ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(
                  documentUrl
              )}`
            : null;


    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(event) =>
                onClose(event.open)
            }
            size="xl"
        >
            <Dialog.Backdrop />

            <Dialog.Positioner>
                <Dialog.Content
                    maxW={{
                        base: "calc(100vw - 24px)",
                        md: "900px",
                    }}
                    h={{
                        base: "90vh",
                        md: "85vh",
                    }}
                    borderRadius="20px"
                    overflow="hidden"
                >
                    {/* HEADER */}
                    <Dialog.Header
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box>
                            <Dialog.Title
                                fontSize="lg"
                                fontWeight="700"
                                color="#2453FF"
                            >
                                {title}
                            </Dialog.Title>

                            <Text
                                fontSize="sm"
                                color="gray.500"
                                mt={1}
                            >
                                {description}
                            </Text>
                        </Box>

                        <Dialog.CloseTrigger
                            asChild
                        >
                            <Button
                                variant="ghost"
                                size="sm"
                                minW="unset"
                                p={2}
                            >
                                <X size={18} />
                            </Button>
                        </Dialog.CloseTrigger>
                    </Dialog.Header>


                    {/* DOCUMENT PREVIEW */}
                    <Dialog.Body
                        p={0}
                        flex="1"
                        minH={0}
                        overflow="hidden"
                        position="relative"
                        bg="#F8FAFC"
                    >
                        {!documentUrl ? (
                            <Flex
                                align="center"
                                justify="center"
                                h="100%"
                                minH="500px"
                                px={6}
                                textAlign="center"
                            >
                                <Text color="gray.500">
                                    This document is
                                    not available.
                                </Text>
                            </Flex>
                        ) : (
                            <>
                                {/* LOADING STATE */}
                                {viewerLoading &&
                                    !viewerError && (
                                        <Flex
                                            position="absolute"
                                            inset="0"
                                            align="center"
                                            justify="center"
                                            direction="column"
                                            gap={3}
                                            bg="white"
                                            zIndex={2}
                                        >
                                            <Spinner
                                                size="lg"
                                                color="#2453FF"
                                            />

                                            <Text
                                                fontSize="sm"
                                                color="gray.500"
                                            >
                                                Loading document...
                                            </Text>
                                        </Flex>
                                    )}


                                {/* ERROR STATE */}
                                {viewerError && (
                                    <Flex
                                        position="absolute"
                                        inset="0"
                                        align="center"
                                        justify="center"
                                        direction="column"
                                        gap={2}
                                        px={6}
                                        textAlign="center"
                                        bg="white"
                                        zIndex={2}
                                    >
                                        <Text
                                            fontWeight="600"
                                            color="#071D54"
                                        >
                                            Unable to preview
                                            this document
                                        </Text>

                                        <Text
                                            fontSize="sm"
                                            color="gray.500"
                                        >
                                            You can still
                                            download the
                                            document below.
                                        </Text>
                                    </Flex>
                                )}


                                {/* GOOGLE DOCUMENT VIEWER */}
                                {!viewerError && (
                                    <iframe
                                        key={googleViewerUrl}
                                        title={title}
                                        src={
                                            googleViewerUrl
                                        }
                                        width="100%"
                                        height="100%"
                                        style={{
                                            border: "none",
                                            minHeight:
                                                "600px",
                                        }}
                                        onLoad={() =>
                                            setViewerLoading(
                                                false
                                            )
                                        }
                                        onError={() =>
                                            setViewerError(
                                                true
                                            )
                                        }
                                    />
                                )}
                            </>
                        )}
                    </Dialog.Body>


                    {/* FOOTER */}
                    <Dialog.Footer
                        borderTop="1px solid"
                        borderColor="gray.100"
                    >
                        <Button
                            variant="outline"
                            onClick={onClose}
                        >
                            Close
                        </Button>

                        <Button
                            bg="#2453FF"
                            color="white"
                            _hover={{
                                bg: "#1E46D8",
                            }}
                            disabled={
                                !documentUrl
                            }
                            onClick={
                                handleDownload
                            }
                        >
                            <Download
                                size={17}
                            />

                            Download
                        </Button>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog.Positioner>
        </Dialog.Root>
    );
};

export default ResourceDocumentDialog;